import { gql, useMutation } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import { Button, Divider, Stack, Typography, useTheme } from '@mui/material'
import Modal from 'components/Modal'
import React, { useContext, useState } from 'react'

import { TaskDataTypeWithId } from '../Create'
import { TASK_FRAGMENT } from '../constants'
import { TIMELINE_FRAGMENT } from '../../constants'
import TimelineContext, { TimelineContextType } from '../context'

const DELETE_TASK = gql`
  ${TIMELINE_FRAGMENT}
  ${TASK_FRAGMENT}
  mutation DELETE_TASK($timelineId: ID!, $taskId: String!) {
    deleteTask(timelineId: $timelineId, taskId: $taskId) {
      ...TimelineFragment
      tasks {
        ...TaskFragment
      }
    }
  }
`

type DeleteTaskPropTypes = {
  task: TaskDataTypeWithId
  onDeleteSuccess?: () => void
}

function Delete({ task, onDeleteSuccess }: DeleteTaskPropTypes) {
  const { timeline } = useContext<TimelineContextType>(TimelineContext)
  const [open, setOpen] = useState<boolean>(false)

  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [deleteTask] = useMutation(DELETE_TASK)

  const handleDelete = () => {
    deleteTask({
      variables: { timelineId: timeline.id, taskId: task.id },
      onCompleted: () => {
        handleClose()
        if (onDeleteSuccess) onDeleteSuccess()
      },
    })
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={420}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Delete - {task.name}
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <Stack spacing={1}>
          <Typography
            sx={{
              ...theme.typography.body1,
              color: theme.palette.text.secondary,
            }}
          >
            Are you sure you want to delete this task? This action cannot be
            undone.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: 'flex-end' }}
          >
            <Button
              color="inherit"
              disableElevation
              onClick={handleClose}
              variant="contained"
              size="small"
            >
              Cancel
            </Button>
            <Button
              color="error"
              disableElevation
              onClick={handleDelete}
              variant="contained"
              size="small"
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Modal>
      <Button color="error" onClick={handleOpen}>
        Delete
      </Button>
    </React.Fragment>
  )
}

export default Delete
